// Hooks for the News screen and the ski mountains. One shared query per key,
// so any number of widgets share one request, and a snapshot keeps the wall
// showing the last copy through a restart.

import { useQuery } from "@tanstack/react-query";
import { MAX_DRIVE_TARGETS, type DrivePayload, type DriveTarget } from "../../shared/drive";
import type { MountainsPayload } from "../../shared/mountains";
import type { F1Payload, League, NewsSourcePayload, TeamPayload, TeamRef } from "../../shared/news";
import { useHousehold } from "../app/useHousehold";
import { useSkyQuery } from "../widgets/sky/api";
import { api } from "./api";

const MIN = 60_000;

export function useNewsSource(id: string, enabled = true) {
  return useSkyQuery<NewsSourcePayload>(["news", "source", id], `/news/source/${encodeURIComponent(id)}`, { refetchMs: 15 * MIN, staleMs: 10 * MIN, enabled });
}

export function useF1(enabled = true) {
  return useSkyQuery<F1Payload>(["news", "f1"], "/news/f1", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled });
}

export function useTeam(ref: TeamRef | null) {
  return useSkyQuery<TeamPayload>(["news", "team", ref?.league, ref?.team], `/news/team?league=${ref?.league ?? ""}&team=${ref?.team ?? ""}`, {
    refetchMs: 15 * MIN,
    staleMs: 10 * MIN,
    enabled: Boolean(ref),
  });
}

export function useLeagueTeams(league: League | null) {
  return useQuery({
    queryKey: ["news", "teams", league],
    queryFn: ({ signal }) => api<{ id: string; abbr: string; name: string; logo: string | null }[]>(`/news/teams?league=${league}`, { signal }),
    staleTime: 24 * 60 * MIN,
    enabled: Boolean(league),
  });
}

export function useMountains() {
  const m = useHousehold().settings.mountains;
  const key = `${m.resorts.join(",")}|${m.powderInches}`;
  return useSkyQuery<MountainsPayload>(["outdoors", "mountains", key], "/outdoors/mountains", { refetchMs: 10 * MIN, staleMs: 5 * MIN, enabled: m.resorts.length > 0 });
}

/**
 * Drive times from home and the chain laws/closures on the way, for any set of
 * places (the Drive screen, the Directions sheet). One request for them all.
 */
export function useDrives(targets: { id: string; target: DriveTarget }[], enabled = true) {
  const { settings } = useHousehold();
  const home = settings.location;
  const to = targets.slice(0, MAX_DRIVE_TARGETS).map(({ id, target }) => (target.address !== undefined ? { id, address: target.address } : { id, lat: target.lat, lon: target.lon }));
  return useQuery({
    queryKey: ["drive", home?.lat, home?.lon, to],
    queryFn: ({ signal }) => api<DrivePayload>("/outdoors/drive", { method: "POST", body: { to }, signal }),
    enabled: enabled && Boolean(home) && to.length > 0,
    refetchInterval: 10 * MIN,
    staleTime: 5 * MIN,
    placeholderData: (prev) => prev,
  });
}
